import {Plugin, PluginKey} from "prosemirror-state";
import {Decoration, DecorationSet} from "prosemirror-view";

export const selectionStatePluginKey = new PluginKey("selectionStatePlugin");

const CUSTOM_NODE_SELECTION_TYPES = new Set([
    "synthetic_projection",
    "userMention",
    "hashtag",
    "link_embed",
]);

const NATIVE_BLOCK_NODE_SELECTION_TYPES = new Set([
    "agenticSection",
]);

function isCustomSelectableNode(node) {
    if (!node) return false;
    if (NATIVE_BLOCK_NODE_SELECTION_TYPES.has(node.type?.name)) return false;
    if (CUSTOM_NODE_SELECTION_TYPES.has(node.type?.name)) return true;
    return Boolean(node.type?.spec?.atom && node.type?.spec?.inline);
}

function buildSelectionDecorations(state) {
    const {selection, doc} = state;
    const decorations = [];

    if (selection?.constructor?.name === "NodeSelection") {
        if (isCustomSelectableNode(selection.node)) {
            decorations.push(Decoration.node(selection.from, selection.to, {class: "pm-node-selection-ring"}));
        }
    }

    if (selection?.constructor?.name === "TextSelection" && !selection.empty) {
        doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            // Include any atom node (inline or block) that isn't natively highlighted by ProseMirror.
            // Previously this was restricted to inline atoms only; block atoms like `figure` need
            // the same treatment because ProseMirror does not call selectNode() during a range selection.
            if (!node.type?.spec?.atom) return;
            if (NATIVE_BLOCK_NODE_SELECTION_TYPES.has(node.type?.name)) return;
            const from = pos;
            const to = pos + node.nodeSize;
            if (from >= selection.from && to <= selection.to) {
                decorations.push(Decoration.node(from, to, {class: "ProseMirror-selectednode"}));
            }
        });
    }

    return DecorationSet.create(doc, decorations);
}

export function selectionStatePlugin() {
    return new Plugin({
        key: selectionStatePluginKey,
        state: {
            init(_, state) {
                return buildSelectionDecorations(state);
            },
            apply(tr, prev, oldState, newState) {
                const selectionChanged = !oldState.selection.eq(newState.selection);
                if (selectionChanged || tr.docChanged) {
                    return buildSelectionDecorations(newState);
                }
                return prev.map(tr.mapping, tr.doc);
            },
        },
        props: {
            decorations(state) {
                return selectionStatePluginKey.getState(state);
            },
        },
    });
}

